import {
  ContractCategory,
  ContractListItemResponse,
  ContractListSortBy,
  ContractStatus,
} from 'compliance-api/generated';
import dayjs, { Dayjs } from 'dayjs';
import { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ArchivedItemAction from '@/modules/admin-dashboard/components/ArchivedItemAction.tsx';
import ContractExportActions from '@/modules/admin-dashboard/components/contractManagement/ContractExportActions.tsx';
import ContractModal from '@/modules/admin-dashboard/components/contractManagement/ContractModal.tsx';
import { ContractStatusBadge } from '@/modules/admin-dashboard/components/contractManagement/ContractStatusBadge.tsx';
import {
  contractCategoryOptions as categoryOptions,
  formatContractCategoryLabel,
} from '@/modules/admin-dashboard/components/contractManagement/contractCategory.ts';
import { contractStatusOptions } from '@/modules/admin-dashboard/components/contractManagement/contractStatus.ts';
import {
  useArchiveContractMutation,
  useContractsListQuery,
  usePartiesMinimalListQuery,
  useRestoreContractMutation,
} from '@/modules/admin-dashboard/queries/contract-management.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import {
  BodyCell,
  Button,
  ConfirmModal,
  DropdownMenu,
  DropdownMenuItem,
  FilterItem,
  ModalProviderContext,
  RadioButton,
  SearchField,
  SelectOption,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
} from 'ui';
import { DotsHorizontalIcon, FilterLinesGrayIcon, PlusIcon } from 'ui/icons';

type ContractsSortBy = ContractListSortBy;

interface ContractsTabProps {
  archive: boolean;
}

const sortOptions: { label: string; value: ContractsSortBy }[] = [
  { label: 'Name (A-Z)', value: ContractListSortBy.NameAsc },
  { label: 'Name (Z-A)', value: ContractListSortBy.NameDesc },
  { label: 'Execution Date (Newest First)', value: ContractListSortBy.ExecutionDateDesc },
  { label: 'Expiry Date (Newest First)', value: ContractListSortBy.ExpiryDateDesc },
];

export const ContractsTab = ({ archive }: ContractsTabProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchValue, setSearchValue] = useState('');
  const [status, setStatus] = useState<ContractStatus | null>(null);
  const [partyId, setPartyId] = useState<number | null>(null);
  const [category, setCategory] = useState<ContractCategory | null>(null);
  const [executionDateRange, setExecutionDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [sortBy, setSortBy] = useState<ContractsSortBy>(ContractListSortBy.ExecutionDateDesc);
  const { mutateAsync: archiveContract } = useArchiveContractMutation();

  const { data: partiesData } = usePartiesMinimalListQuery({
    page: 1,
    page_size: 100,
  });

  const partyOptions = useMemo<SelectOption<number>[]>(() => {
    return (partiesData?.pages || [])
      .flatMap(page => page.data)
      .map(party => ({
        value: party.id,
        label: party.name,
      }));
  }, [partiesData?.pages]);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching, isError } =
    useContractsListQuery({
      page: 1,
      page_size: 30,
      search: searchValue || undefined,
      status: status || undefined,
      party_id: partyId || undefined,
      category: category || undefined,
      sort_by: sortBy,
      is_active: !archive,
    });

  const selectedSort = useMemo(
    () => sortOptions.find(({ value }) => value === sortBy) || sortOptions[0],
    [sortBy],
  );

  const contracts = useMemo(() => {
    const items = (data?.pages || []).flatMap(page => page.data);
    return executionDateRange
      ? items.filter(contract => {
          if (!contract.execution_date) return false;
          const executionDate = dayjs(contract.execution_date);
          return (
            executionDate.isSame(executionDateRange[0], 'day') ||
            executionDate.isSame(executionDateRange[1], 'day') ||
            (executionDate.isAfter(executionDateRange[0], 'day') &&
              executionDate.isBefore(executionDateRange[1], 'day'))
          );
        })
      : items;
  }, [data?.pages, executionDateRange]);

  const handleSelectSort = useCallback((value: ContractsSortBy) => {
    setSortBy(value);
  }, []);

  const functionalSortOptions = useMemo<DropdownMenuItem<ReactNode>[]>(
    () =>
      sortOptions.map(({ label, value }) => ({
        label: <RadioButton items={[{ label, value }]} value={sortBy} onChange={() => 0} />,
        value,
        onClick: () => handleSelectSort(value),
      })),
    [handleSelectSort, sortBy],
  );

  const handleOpenContractModal = useCallback(
    (contract?: ContractListItemResponse) => {
      if (!contract) {
        openModal(ContractModal);
        return;
      }

      navigate({
        pathname: adminDashboardPaths.dynamic.contract(contract.id),
        search: location.search,
      });
    },
    [location.search, navigate, openModal],
  );

  const headItems = useMemo<TableHeadColumn[]>(
    () => [
      // { title: <CustomCheckbox disabled />, className: 'w-[44px] min-w-[44px]' },
      { title: 'Name' },
      { title: 'Status' },
      { title: 'Counter Party Name' },
      { title: 'Category' },
      { title: 'Execution date' },
      { title: 'Expiry Date' },
      { title: 'Currency' },
      { title: '' },
    ],
    [],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(
    () =>
      contracts.map(contract => ({
        cells: [
          ...getContractCells(contract),
          getContractActionCell({
            contract,
            archive,
            onArchive: () =>
              openModal(ConfirmModal, {
                onConfirm: async () => {
                  await archiveContract(contract.id);
                },
                title: (
                  <p>
                    Are you sure you want to archive{' '}
                    <span className="text-blue-700">{contract.contract_name || '-'}</span>?
                  </p>
                ),
              }),
          }),
        ],
        onClick: archive ? undefined : () => handleOpenContractModal(contract),
      })),
    [archive, archiveContract, contracts, handleOpenContractModal, openModal],
  );

  return (
    <div className="flex w-full flex-col">
      <div className="flex flex-col gap-3 px-3 py-3 sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <SearchField
            placeholder="Search"
            value={searchValue}
            onChange={setSearchValue}
            isLoading={isFetching && !isLoading}
            allowClear
            className="w-full sm:w-[240px]"
          />

          <FilterItem
            type="select"
            placeholder="Status"
            value={status || undefined}
            options={contractStatusOptions}
            onChange={value => setStatus(value || null)}
            className="w-fit"
            classes={{
              element: 'w-[128px]',
            }}
          />

          <FilterItem
            type="select"
            placeholder="Parties"
            value={partyId || undefined}
            options={partyOptions}
            onChange={value => setPartyId(value || null)}
            className="w-fit"
            classes={{
              element: 'w-[156px]',
            }}
          />

          <FilterItem
            type="date"
            placeholder="Execution date"
            value={executionDateRange || undefined}
            onChange={setExecutionDateRange}
            className="w-fit"
            classes={{
              element: 'w-fit',
            }}
          />

          <FilterItem
            type="select"
            placeholder="Category"
            value={category || undefined}
            options={categoryOptions}
            onChange={value => setCategory(value || null)}
            className="w-fit"
            classes={{
              element: 'w-[156px]',
            }}
          />

          <DropdownMenu
            className="w-[220px]"
            containerClassName="w-fit"
            placement="bottom"
            button={
              <div className="flex h-10 w-fit items-center gap-2 rounded px-2 text-sm leading-[22px] text-gray-900 hover:bg-gray-100">
                <img src={FilterLinesGrayIcon} alt="sort" className="h-5 w-5" />
                <span className="text-gray-500">Sort:</span>
                <span className="font-medium">{selectedSort.label}</span>
              </div>
            }
            items={functionalSortOptions}
          />

          {isFetching && <Spinner size="sm" variant="primary" className="m-0" />}

          <div className="ml-auto flex items-center gap-2">
            <ContractExportActions
              filters={{
                searchValue,
                status,
                partyId,
                category,
                executionDateRange: executionDateRange
                  ? [
                      executionDateRange[0].format('YYYY-MM-DD'),
                      executionDateRange[1].format('YYYY-MM-DD'),
                    ]
                  : null,
                sortBy,
                archive,
              }}
            />

            <Button className="w-fit gap-2" onClick={() => handleOpenContractModal()}>
              <img src={PlusIcon} alt="" className="h-5 w-5 invert" />
              <span>New contract</span>
            </Button>
          </div>
        </div>
      </div>

      <Table
        className="my-0"
        classNameHead="border-t-0"
        withRounded={false}
        withBorder={true}
        withBorderX={false}
        headItems={headItems}
        items={bodyItems}
        isLoading={isLoading}
        isError={isError}
        isSearchUsed={
          !!searchValue || !!status || !!partyId || !!category || !!executionDateRange || archive
        }
        hasNextPageInfinite={hasNextPage}
        fetchNextPageInfinite={fetchNextPage}
        blockIdInfinite="scrollable"
      />
    </div>
  );
};

const getContractCells = (contract: ContractListItemResponse): BodyCell[] => {
  const partyLabel = formatPartyNames(contract.parties);

  return [
    // {
    //   cell: <CustomCheckbox disabled />,
    //   className: 'w-[44px] min-w-[44px]',
    // },
    {
      cell: <span className="font-medium text-gray-900">{contract.contract_name || '-'}</span>,
    },
    {
      cell: <ContractStatusBadge status={contract.status} />,
    },
    {
      cell: <span className="min-w-0 text-gray-900">{partyLabel}</span>,
    },
    {
      cell: <span>{formatContractCategoryLabel(contract.category)}</span>,
    },
    {
      cell: (
        <span>
          {contract.execution_date ? dayjs(contract.execution_date).format('DD MMM YYYY') : '-'}
        </span>
      ),
    },
    {
      cell: (
        <span>
          {contract.expiry_date ? dayjs(contract.expiry_date).format('DD MMM YYYY') : '-'}
        </span>
      ),
    },
    {
      cell: <span>{contract.currency_code?.toUpperCase()}</span>,
    },
  ];
};

const getContractActionCell = ({
  contract,
  archive,
  onArchive,
}: {
  contract: ContractListItemResponse;
  archive: boolean;
  onArchive: () => void;
}): BodyCell => ({
  cell: (
    <div className="flex justify-end" onClick={event => event.stopPropagation()}>
      {archive ? (
        <ArchivedItemAction id={contract.id} mutation={useRestoreContractMutation} />
      ) : (
        <DropdownMenu
          autoPlace
          items={[
            {
              label: 'Archive',
              onClick: onArchive,
            },
          ]}
          button={
            <button className="flex items-center justify-center">
              <img src={DotsHorizontalIcon} alt="Actions" className="w-6 h-6 min-w-6 min-h-6" />
            </button>
          }
          className="w-[180px] divide-white"
        />
      )}
    </div>
  ),
});

const formatPartyNames = (parties: ContractListItemResponse['parties']) => {
  if (!parties?.length) return '-';
  if (parties.length === 1) return parties[0]?.name || '-';
  if (parties.length === 2) return `${parties[0]?.name || '-'}\n${parties[1]?.name || '-'}`;
  return `${parties[0]?.name || '-'}\n${parties[1]?.name || '-'}\nand ${parties.length - 2} more`;
};

export default ContractsTab;
